import React, { useState, useRef } from 'react';
import { X, AlertTriangle, Upload, Loader2, CheckCircle, Camera } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';
import { getToken } from '../utils/tokenManager';
import {
  ProblemSeverity,
  ProblemCategory,
  SeverityLabels,
  CategoryLabels,
  CreateProblemReportRequest
} from '../types/problemReport';

interface ReportProblemModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormErrors {
  description?: string;
  screenshot?: string;
}

const MAX_SCREENSHOT_SIZE = 5 * 1024 * 1024; // 5MB

const ReportProblemModal: React.FC<ReportProblemModalProps> = ({ isOpen, onClose }) => {
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<ProblemSeverity>(ProblemSeverity.Medium);
  const [category, setCategory] = useState<ProblemCategory>(ProblemCategory.Other);
  const [screenshotData, setScreenshotData] = useState<string | null>(null);
  const [screenshotContentType, setScreenshotContentType] = useState<string | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!description.trim()) {
      newErrors.description = 'Description is required';
    } else if (description.length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    } else if (description.length > 4000) {
      newErrors.description = 'Description must not exceed 4000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, screenshot: 'Please select an image file' }));
      return;
    }

    // Validate file size
    if (file.size > MAX_SCREENSHOT_SIZE) {
      setErrors(prev => ({ ...prev, screenshot: 'Screenshot must be less than 5MB' }));
      return;
    }

    // Clear any previous error
    setErrors(prev => ({ ...prev, screenshot: undefined }));

    // Read file as base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      // Extract the base64 part (remove the data:image/xxx;base64, prefix)
      const base64Data = result.split(',')[1];
      setScreenshotData(base64Data);
      setScreenshotContentType(file.type);
      setScreenshotPreview(result);
    };
    reader.readAsDataURL(file);
  };

  const removeScreenshot = () => {
    setScreenshotData(null);
    setScreenshotContentType(null);
    setScreenshotPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const token = getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const request: CreateProblemReportRequest = {
        description: description.trim(),
        severity,
        category,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
        ...(screenshotData && {
          screenshotData,
          screenshotContentType: screenshotContentType || 'image/png'
        })
      };

      const response = await fetch(API_ENDPOINTS.PROBLEM_REPORTS.CREATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(request)
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus('success');
        setSubmitMessage(data.message || 'Thank you! Your report has been submitted.');

        // Reset form after success
        setTimeout(() => {
          resetForm();
          onClose();
        }, 2000);
      } else {
        setSubmitStatus('error');
        setSubmitMessage(data.detail || data.error || 'Failed to submit report. Please try again.');
      }
    } catch {
      setSubmitStatus('error');
      setSubmitMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setDescription('');
    setSeverity(ProblemSeverity.Medium);
    setCategory(ProblemCategory.Other);
    setScreenshotData(null);
    setScreenshotContentType(null);
    setScreenshotPreview(null);
    setErrors({});
    setSubmitStatus('idle');
    setSubmitMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-slate-900 border border-white/10 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#AD0000] to-red-700 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">Report a Problem</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 text-slate-400 hover:text-white transition-colors rounded-lg hover:bg-white/5 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Success/Error Message */}
          {submitStatus !== 'idle' && (
            <div
              className={`p-4 rounded-xl flex items-start space-x-3 ${
                submitStatus === 'success'
                  ? 'bg-green-500/20 border border-green-500/30'
                  : 'bg-red-500/20 border border-red-500/30'
              }`}
            >
              {submitStatus === 'success' ? (
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              )}
              <p className={submitStatus === 'success' ? 'text-green-200' : 'text-red-200'}>
                {submitMessage}
              </p>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-300">
              What's the problem? <span className="text-red-400">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                if (errors.description) {
                  setErrors(prev => ({ ...prev, description: undefined }));
                }
              }}
              rows={4}
              placeholder="Please describe the issue you're experiencing..."
              className={`w-full px-4 py-3 rounded-xl border ${
                errors.description ? 'border-red-500' : 'border-white/10'
              } bg-white/5 text-white placeholder-slate-500 focus:outline-none focus:border-[#AD0000]/50 focus:ring-2 focus:ring-[#AD0000]/20 transition-all resize-none`}
              disabled={isSubmitting || submitStatus === 'success'}
            />
            <div className="flex justify-between items-center mt-1">
              {errors.description ? (
                <p className="text-sm text-red-400">{errors.description}</p>
              ) : (
                <span />
              )}
              <span className="text-xs text-slate-500">{description.length}/4000</span>
            </div>
          </div>

          {/* Severity & Category Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Severity */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">
                Severity
              </label>
              <select
                value={severity}
                onChange={(e) => setSeverity(Number(e.target.value) as ProblemSeverity)}
                className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-white focus:outline-none focus:border-[#AD0000]/50 focus:ring-2 focus:ring-[#AD0000]/20 transition-all"
                disabled={isSubmitting || submitStatus === 'success'}
              >
                {Object.entries(SeverityLabels).map(([value, label]) => (
                  <option key={value} value={value} className="bg-slate-900">
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(Number(e.target.value) as ProblemCategory)}
                className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-white focus:outline-none focus:border-[#AD0000]/50 focus:ring-2 focus:ring-[#AD0000]/20 transition-all"
                disabled={isSubmitting || submitStatus === 'success'}
              >
                {Object.entries(CategoryLabels).map(([value, label]) => (
                  <option key={value} value={value} className="bg-slate-900">
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Screenshot Upload */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-300">
              Screenshot (optional)
            </label>

            {screenshotPreview ? (
              <div className="relative rounded-xl overflow-hidden border border-white/10">
                <img
                  src={screenshotPreview}
                  alt="Screenshot preview"
                  className="w-full max-h-48 object-contain bg-black/30"
                />
                <button
                  type="button"
                  onClick={removeScreenshot}
                  className="absolute top-2 right-2 p-1.5 bg-red-500/80 hover:bg-red-500 rounded-lg transition-colors"
                  disabled={isSubmitting || submitStatus === 'success'}
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-white/20 hover:border-[#AD0000]/50 rounded-xl p-6 text-center cursor-pointer transition-colors"
              >
                <div className="flex flex-col items-center space-y-2">
                  <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center">
                    <Camera className="w-6 h-6 text-slate-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-300">Click to upload a screenshot</p>
                    <p className="text-xs text-slate-500">PNG, JPG, GIF up to 5MB</p>
                  </div>
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
              disabled={isSubmitting || submitStatus === 'success'}
            />

            {errors.screenshot && (
              <p className="mt-1 text-sm text-red-400">{errors.screenshot}</p>
            )}
          </div>

          {/* Page URL (auto-captured, displayed for transparency) */}
          <div className="text-xs text-slate-500 bg-white/5 rounded-lg px-3 py-2">
            <span className="font-medium">Current page:</span>{' '}
            <span className="break-all">{window.location.href}</span>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting || submitStatus === 'success'}
            className="w-full px-6 py-3 bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-red-500/20 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Submitting...</span>
              </>
            ) : submitStatus === 'success' ? (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>Submitted!</span>
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                <span>Submit Report</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ReportProblemModal;
